import { useTheme } from "@/hooks/useTheme"

interface LogoProps {
  className?: string
  bgFill?: string
  width?: number | string
  height?: number | string
}

export function Logo({ className = "h-8", bgFill, width, height }: LogoProps) {
  const { theme } = useTheme()
  const isDark = theme === "dark"

  // Default backgrounds: white for light, #0A0A0A for dark
  const bg = bgFill ?? (isDark ? "#0A0A0A" : "white")

  // Theme-specific colors
  const colors = isDark
    ? {
        stroke1: "#9B9B9B",
        stroke2: "#8A8A8A",
        fill1: "#595859",
        fill2: "#232322",
        fill3: "#B5B4B4",
        fill4: "#FEFEFE",
      }
    : {
        stroke1: "#6B6B6B",
        stroke2: "#7A7A7A",
        fill1: "#A6A7A6",
        fill2: "#DCDCDD",
        fill3: "#4A4B4B",
        fill4: "#010101",
      }

  return (
    <svg
      className={className}
      viewBox="0 70 592 460"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
    >
      <g clipPath="url(#clip0_logo)">
        <rect width="592" height="602" fill={bg} />
        <ellipse cx="296" cy="301" rx="296" ry="301" fill={bg} />
        <path
          d="M296 75.5C446.216 75.5 567.5 176.707 567.5 301C567.5 425.293 446.216 526.5 296 526.5C145.784 526.5 24.5 425.293 24.5 301C24.5 176.707 145.784 75.5 296 75.5Z"
          stroke={colors.stroke1}
          strokeWidth="3"
        />
        <ellipse cx="296" cy="302.5" rx="251" ry="259.5" fill={bg} />
        <path
          d="M294.5 99.5C424.514 99.5 529.5 189.701 529.5 300.5C529.5 411.299 424.514 501.5 294.5 501.5C164.486 501.5 59.5 411.299 59.5 300.5C59.5 189.701 164.486 99.5 294.5 99.5Z"
          stroke={colors.stroke2}
          strokeWidth="3"
        />
        <ellipse cx="294.5" cy="302.5" rx="212.5" ry="215.5" fill={bg} />
        <path
          d="M289.766 118.235C343.02 116.363 395.149 136.865 435.082 172.063C509.703 237.837 514.372 351.693 445.763 423.861C407.383 464.233 357.358 484.733 301.625 487.012C247.104 487.731 199.352 473.158 157.433 436.453C120.581 404.185 98.174 359.374 95.859 310.04C93.716 264.59 109.733 220.154 140.379 186.522C180.302 142.749 231.034 120.866 289.766 118.235Z"
          fill={colors.fill1}
        />
        <path
          d="M295.484 122.236C347.389 121.51 398.216 142.896 436.117 178.396C512.524 249.964 507.263 369.011 427.4 435.784C388.777 468.076 345.431 483.056 294.375 483.346C242.107 483.993 191.668 464.122 153.886 427.998C119.881 395.232 100.527 353.45 99.577 305.979C98.611 257.766 115.875 217.435 149.207 183.03C179.56 152.008 219.069 131.548 261.92 124.66C274.416 122.694 282.941 122.421 295.484 122.236Z"
          fill={colors.fill2}
        />
        <path
          d="M295.484 122.236C347.389 121.51 398.216 142.896 436.117 178.396C512.524 249.964 507.263 369.011 427.4 435.784C388.777 468.076 345.431 483.056 294.375 483.346C242.107 483.993 191.668 464.122 153.886 427.998C119.881 395.232 100.527 353.45 99.577 305.979C98.611 257.766 115.875 217.435 149.207 183.03C179.56 152.008 219.069 131.548 261.92 124.66C274.416 122.694 282.941 122.421 295.484 122.236Z"
          fill={bg}
        />
        <path
          d="M285.971 134.746C365.807 130.488 437.647 182.912 457.944 260.242C478.242 337.571 441.411 418.52 369.776 454.021C298.141 489.521 211.421 469.801 162.181 406.815C112.942 343.828 114.737 254.912 166.479 193.964C196.472 158.633 239.692 137.215 285.971 134.746Z"
          fill={colors.fill3}
        />
        <path
          d="M291.72 140.047C303.735 140.331 313.797 140.812 325.711 143.034C360.634 149.794 392.605 167.209 417.226 192.883C446.495 223.544 462.841 263.254 461.816 305.799C460.896 349.082 442.56 390.166 410.957 419.755C378.247 450.381 334.845 466.981 290.047 465.998C245.402 464.782 203.092 445.789 172.514 413.237C143.075 382.047 127.116 340.509 128.098 297.631C129.121 262.711 141.766 228.967 164.454 202.149C166.078 200.229 168.168 198.038 169.579 195.996C175.407 190.631 180.089 185.312 186.321 180.087C211.143 159.272 241.327 145.871 273.415 141.42C279.108 140.603 285.777 140.655 291.72 140.047Z"
          fill={bg}
        />
        <path
          d="M282.366 152.905C365.632 145.951 438.723 207.924 445.479 291.206C452.236 374.488 390.091 447.432 306.793 453.992C223.773 460.529 151.126 398.637 144.392 315.633C137.658 232.629 199.378 159.836 282.366 152.905Z"
          fill={colors.fill4}
        />
        <path
          d="M289.636 167.749C363.433 164.836 425.624 222.29 428.554 296.086C431.485 369.883 374.046 432.088 300.25 435.036C226.429 437.985 164.2 380.522 161.268 306.701C158.337 232.88 215.814 170.664 289.636 167.749Z"
          fill={bg}
        />
      </g>
      <defs>
        <clipPath id="clip0_logo">
          <rect width="592" height="602" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}
